import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import PasswordStrengthReport from '@sophocles/components/PasswordStrengthReport';
import { useSetUserPasswordMutation } from '@sophocles/features/entities/users';
import { addToast } from '@sophocles/features/toast';
import { getPasswordVerifier } from '@sophocles/utils';
import { parseApiError } from '@sophocles/utils/apiErrors';

// The only field name the set_password endpoint reports validation errors
// against; anything else downgrades to the dialog's error banner.
const PASSWORD_FIELDS = ['password'];

// Self-contained password-change modal.  Mirrors the validation
// rules used by the legacy inline form (length, character classes)
// and posts to the same `/users/:id/set_password/` endpoint via the
// odl-app setUserPassword mutation.
export default function ChangePasswordDialog({ open, onClose, userId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [setUserPassword] = useSetUserPasswordMutation();

  const [password, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const verifier = useMemo(() => getPasswordVerifier(), []);
  const report = useMemo(() => verifier(password), [verifier, password]);
  const isValid = report.isValid;
  const matches = password.length > 0 && password === confirm;
  const canSubmit = isValid && matches && !submitting;

  const handleClose = () => {
    if (submitting) return;
    setPwd('');
    setConfirm('');
    setShowPassword(false);
    setPasswordError(null);
    setNonFieldMessages([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !userId) return;
    setSubmitting(true);
    setPasswordError(null);
    setNonFieldMessages([]);
    try {
      await setUserPassword({ id: userId, password }).unwrap();
      dispatch(addToast({ message: t('userSettings.passwordUpdated', 'Password updated.'), level: 'success' }));
      handleClose();
    } catch (error) {
      const { fieldErrors, nonFieldMessages: messages } = parseApiError(error, {
        knownFields: PASSWORD_FIELDS,
        genericMessage: t('userSettings.changePasswordFailed', 'Failed to change password'),
      });
      setPasswordError(fieldErrors.password?.join(' ') ?? null);
      setNonFieldMessages(messages);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('userSettings.changePassword', 'Change Password')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('userSettings.passwordMessage', 'Choose a strong password you have not used before.')}
          </Typography>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
            <PasswordStrengthReport report={report.metrics} textAlign="left" />
          </Box>
          <TextField
            label={t('general.password', 'Password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPwd(e.target.value)}
            autoComplete="new-password"
            fullWidth
            error={Boolean(passwordError)}
            helperText={passwordError ?? ' '}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('userSettings.togglePasswordVisibility', 'Toggle password visibility')}
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('userSettings.confirmPassword', 'Confirm Password')}
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
            fullWidth
            error={confirm.length > 0 && !matches}
            helperText={
              confirm.length > 0 && !matches ? t('userSettings.passwordsNotMatched', 'Passwords do not match.') : ' '
            }
          />
          <FormErrorAlert messages={nonFieldMessages} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('actions.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {t('userSettings.savePassword', 'Save Password')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
